import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import DevicesIcon from '@mui/icons-material/Devices'
import LogoutIcon from '@mui/icons-material/Logout'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'

import {
    useGetSessionsQuery,
    useRevokeSessionMutation,
    useGetUserSessionsQuery,
    useRevokeUserSessionMutation,
    useRevokeAllUserSessionsMutation
} from '@/store/api/authApi'

// Resumen legible del user-agent, sin pretender un parseo completo de dispositivo/navegador
const summarizeUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown device'
    const browser = /Edg\//.test(userAgent)
        ? 'Edge'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Firefox\//.test(userAgent)
            ? 'Firefox'
            : /Safari\//.test(userAgent)
              ? 'Safari'
              : 'Browser'
    const os = /Windows/.test(userAgent)
        ? 'Windows'
        : /Android/.test(userAgent)
          ? 'Android'
          : /iPhone|iPad/.test(userAgent)
            ? 'iOS'
            : /Mac OS/.test(userAgent)
              ? 'macOS'
              : /Linux/.test(userAgent)
                ? 'Linux'
                : ''

    return [browser, os].filter(Boolean).join(' · ')
}

// Sin userId gestiona la sesión propia; con userId (admin/supervisor) gestiona la de otro usuario
const ActiveSessionsDialog = ({ open, onClose, userId }) => {
    const { t } = useTranslation()

    const ownSessions = useGetSessionsQuery(undefined, { skip: !open || !!userId })
    const otherSessions = useGetUserSessionsQuery(userId, { skip: !open || !userId })
    const { data: sessions, isFetching, isError } = userId ? otherSessions : ownSessions

    const [revokeSession, { isLoading: isRevokingOwn }] = useRevokeSessionMutation()
    const [revokeUserSession, { isLoading: isRevokingOther }] = useRevokeUserSessionMutation()
    const [revokeAllUserSessions, { isLoading: isRevokingAll }] = useRevokeAllUserSessionsMutation()
    const isRevoking = isRevokingOwn || isRevokingOther || isRevokingAll

    const handleRevoke = (tid) => {
        const action = userId ? revokeUserSession({ userId, tid }) : revokeSession(tid)
        action.catch(() => {})
    }

    const handleRevokeAll = () => {
        if (!userId) return
        revokeAllUserSessions(userId).catch(() => {})
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
            <DialogTitle>{t('Active sessions')}</DialogTitle>

            <DialogContent dividers>
                {isFetching && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {isError && <Alert severity='error'>{t('Could not load active sessions')}</Alert>}

                {!isFetching && !isError && sessions?.length === 0 && (
                    <Typography color='text.secondary'>{t('No active sessions found')}</Typography>
                )}

                {!isFetching && sessions?.length > 0 && (
                    <List disablePadding>
                        {sessions.map((session) => (
                            <ListItem
                                key={session.tid}
                                divider
                                secondaryAction={
                                    <Tooltip title={t('Revoke session')}>
                                        <span>
                                            <IconButton
                                                edge='end'
                                                color='error'
                                                disabled={isRevoking}
                                                onClick={() => handleRevoke(session.tid)}>
                                                <LogoutIcon fontSize='small' />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                }>
                                <ListItemIcon>
                                    <DevicesIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={summarizeUserAgent(session.userAgent)}
                                    secondary={
                                        <>
                                            {session.ipAddress || t('Unknown IP')}
                                            {' · '}
                                            {t('Last used')}
                                            {': '}
                                            {session.lastUsedAt
                                                ? dayjs(session.lastUsedAt).format(
                                                      'DD/MM/YYYY HH:mm'
                                                  )
                                                : '-'}
                                        </>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions>
                {userId && sessions?.length > 0 && (
                    <Button
                        color='error'
                        startIcon={<DeleteSweepIcon />}
                        disabled={isRevoking}
                        onClick={handleRevokeAll}
                        sx={{ mr: 'auto' }}>
                        {t('Revoke all sessions')}
                    </Button>
                )}
                <Button onClick={onClose}>{t('Close')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export default ActiveSessionsDialog
